import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client.js';

/**
 * A minimal async-data hook, for the requests that are **not** part of the read model.
 *
 * It exposes `error` and `reload` rather than swallowing failures, because PRD §6.5 requires
 * an **error banner with retry** — not a spinner that never resolves and not a silently empty
 * table, both of which read as "there is no data" when the truth is "we could not fetch it".
 *
 * The generation counter discards a response whose request has been superseded, so flipping a
 * filter twice quickly cannot leave the first response painted over the second.
 *
 * ⚠ **No caching here.** Snapshot data is cached and prefetched by the client store — pages read
 * it through `useQuery` (`store/StoreProvider.tsx`), which is TTL'd, persisted, and guarded
 * against mixing two snapshots. This hook briefly grew a cache of its own and it was removed:
 * two caches with two lifetimes over the same responses is how a page ends up showing figures
 * neither of them believes. What is left for this hook is everything that is not snapshot data —
 * the Settings directory listing being the whole of it today.
 */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  const generation = useRef(0);
  const callback = useRef(fn);
  callback.current = fn;

  // Collapsing the caller's dependencies to a stable string keeps the effect's own dependency
  // array a fixed length, which is what the rules-of-hooks lint requires.
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    const current = ++generation.current;
    let cancelled = false;

    setLoading(true);
    setError(null);

    callback
      .current()
      .then((result) => {
        if (cancelled || current !== generation.current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (cancelled || current !== generation.current) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled || current !== generation.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // The dependency array is supplied by the caller: this hook exists precisely so a page can
    // say which values should retrigger a fetch. `callback` is held in a ref so a new closure
    // on every render does not count as a change.
  }, [depsKey, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload };
}

export function isAuthError(error: Error | null): error is ApiError {
  return error instanceof ApiError && (error.isUnauthenticated || error.isDeactivated);
}
